import type { StructuredContentDocument } from '@repo/contracts/multi-use/structured-content/types';
import { css } from '@repo/pandacss/styled-system/css';
import { flex, stack } from '@repo/pandacss/styled-system/patterns';
import { BrandIcon, type BrandIconType } from '@repo/ui-v2/BrandIcon';
import { Details } from '@repo/ui-v2/Details';
import { MdExpandMore } from 'react-icons/md';
import { renderStructuredContent } from '../../rendererContext';
import type { RendererProps } from '../../types';

type ExpandCardProps = StructuredContentDocument<{
  icon?: BrandIconType;
  title1?: string;
  title2?: string;
  children: StructuredContentDocument;
  rendererProps?: RendererProps;
}>;

export function ExpandCard({
  props: { children, icon, title1, title2, rendererProps },
}: ExpandCardProps) {
  return (
    <Details
      variant="simple"
      detailsProps={{
        className: css({
          borderTopLeftRadius: '3',
          borderTopRightRadius: '3',
          borderBottomRightRadius: '3',
          borderBottomLeftRadius: '3',
          minHeight: { base: '[unset]', sm: '[152px]', md: '[177px]' },
          paddingTop: '4',
          paddingRight: '4',
          paddingBottom: '4',
          paddingLeft: '4',
          backgroundColor: 'white',
          justifyContent: 'space-between',
          borderTopWidth: 'thin',
          borderRightWidth: 'thin',
          borderBottomWidth: 'thin',
          borderLeftWidth: 'thin',
          borderTopStyle: 'solid',
          borderRightStyle: 'solid',
          borderBottomStyle: 'solid',
          borderLeftStyle: 'solid',
          borderTopColor: 'transparent',
          borderRightColor: 'transparent',
          borderBottomColor: 'transparent',
          borderLeftColor: 'transparent',
          transitionProperty: '[border-color]',
          transitionDuration: '[0.3s]',
          transitionTimingFunction: 'ease',
          WebkitTapHighlightColor: 'transparent',
          '&:hover': {
            borderTopColor: 'green.main',
            borderRightColor: 'green.main',
            borderBottomColor: 'green.main',
            borderLeftColor: 'green.main',
            cursor: 'pointer',
          },
        }),
      }}
      SummaryComponent={
        <div
          className={stack({
            justifyContent: 'space-between',
            width: 'full',
            columnGap: { base: '4', lg: '6' },
            rowGap: { base: '4', lg: '6' },
          })}
        >
          <div
            className={flex({
              justifyContent: 'space-between',
            })}
          >
            {icon && (
              <BrandIcon
                name={icon}
                size={24}
                className={css({
                  width: { base: '6', lg: '8' },
                  height: { base: '6', lg: '8' },
                  filter:
                    '[brightness(0) saturate(100%) invert(36%) sepia(12%) saturate(4565%) hue-rotate(118deg) brightness(97%) contrast(103%)]',
                })}
              />
            )}
            <MdExpandMore
              className={css({
                backgroundColor: 'neutral.lightest',
                color: 'neutral.light',
                fontSize: 'xxl',
                borderTopLeftRadius: '[100%]',
                borderTopRightRadius: '[100%]',
                borderBottomRightRadius: '[100%]',
                borderBottomLeftRadius: '[100%]',
                transitionProperty: '[transform]',
                transitionDuration: '[0.3s]',
                transitionTimingFunction: 'ease',
                justifySelf: 'flex-end',
                '[open] > summary &': { transform: '[rotate(180deg)]' },
              })}
            />
          </div>

          <div className={stack({})}>
            {/* Primary Title */}
            <p
              className={css({
                color: 'green.main',
                fontSize: {
                  base: 'xl',
                  lg: '[26px]',
                },
                fontWeight: 'medium',
              })}
            >
              {title1}
            </p>
            {/* Secondary Title */}
            <p
              className={css({
                color: 'neutral.darker',
                fontWeight: 'medium',
              })}
            >
              {title2}
            </p>
          </div>
        </div>
      }
    >
      <div
        className={css({
          marginTop: '2',
          '& p': {
            color: 'neutral.darker',
            fontSize: '[13px !important]',
            lineHeight: '[150%]',
          },
        })}
      >
        {renderStructuredContent(children, rendererProps)}
      </div>
    </Details>
  );
}
